import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type PreInspectionLog,
  type IncidentLog,
  moodZones,
  physicalStateOptions,
  belongingOptions,
  getIncidentTypeById,
} from '@/mocks/preInspection';

interface PreInspectionLogListProps {
  open: boolean;
  onClose: () => void;
  selectedDate: string;
  preInspectionLogs: PreInspectionLog[];
  incidentLogs: IncidentLog[];
}

type TabType = 'preInspection' | 'incidents';

export default function PreInspectionLogList({
  open,
  onClose,
  selectedDate,
  preInspectionLogs,
  incidentLogs,
}: PreInspectionLogListProps) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<TabType>('preInspection');

  const filteredPiLogs = useMemo(() => {
    return preInspectionLogs
      .filter((l) => l.date === selectedDate)
      .sort((a, b) => b.timestamp.localeCompare(a.timestamp));
  }, [preInspectionLogs, selectedDate]);

  const filteredIncidentLogs = useMemo(() => {
    return incidentLogs
      .filter((l) => l.date === selectedDate)
      .sort((a, b) => b.timestamp.localeCompare(a.timestamp));
  }, [incidentLogs, selectedDate]);

  const formatDisplayDate = (dateStr: string) => {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  if (!open) return null;

  const zoneBorderColors: Record<string, string> = {
    blue: 'border-blue-300',
    green: 'border-green-300',
    yellow: 'border-yellow-300',
    red: 'border-red-300',
  };

  const zoneBgColors: Record<string, string> = {
    blue: 'bg-blue-50',
    green: 'bg-green-50',
    yellow: 'bg-yellow-50',
    red: 'bg-red-50',
  };

  const renderPiLogCard = (log: PreInspectionLog) => {
    const mood = moodZones.flatMap((z) => z.moods).find((m) => m.id === log.moodId);
    const zone = moodZones.find((z) => z.id === log.moodZone);
    return (
      <div
        key={log.id}
        className={`p-3 rounded-xl border ${zoneBorderColors[log.moodZone] || 'border-background-200'} ${zoneBgColors[log.moodZone] || 'bg-background-50'}`}
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-7 h-7 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 text-[10px] font-heading font-700 flex-shrink-0">
              {log.studentName.split(' ').map((n) => n[0]).join('')}
            </div>
            <div className="min-w-0">
              <p className="text-xs font-label font-medium text-foreground-800 truncate">{log.studentName}</p>
              <p className="text-[10px] text-foreground-400 font-label">{log.className}</p>
            </div>
          </div>
          <span className={`px-2 py-0.5 text-[10px] font-label font-medium rounded-full ${zoneBgColors[log.moodZone]} ${zoneBorderColors[log.moodZone]} flex-shrink-0`}>
            {mood?.emoji} {mood?.label}
          </span>
        </div>

        {log.physicalStates.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-1.5">
            {log.physicalStates.map((ps) => {
              const opt = physicalStateOptions.find((o) => o.id === ps);
              return (
                <span key={ps} className="px-1.5 py-0.5 text-[10px] font-label rounded-full bg-background-100 text-foreground-500 border border-background-200">
                  {opt?.label || ps}
                </span>
              );
            })}
          </div>
        )}

        {log.belongings.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-1.5">
            {log.belongings.map((b) => {
              const opt = belongingOptions.find((o) => o.id === b);
              return (
                <span key={b} className="px-1.5 py-0.5 text-[10px] font-label rounded-full bg-accent-50 text-accent-600 border border-accent-100">
                  <i className={`${opt?.icon || 'ri-check-line'} text-[9px] mr-0.5`} />
                  {opt?.label || b}
                </span>
              );
            })}
          </div>
        )}

        {log.notes && (
          <p className="text-[11px] text-foreground-600 font-label mb-1.5 leading-relaxed">{log.notes}</p>
        )}

        <div className="flex items-center gap-3 text-[10px] text-foreground-400 font-label flex-wrap">
          <span>{new Date(log.timestamp).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</span>
          <span>{log.createdBy}</span>
          {log.notifyParent && log.notificationSent && (
            <span className="flex items-center gap-1 text-green-600">
              <i className="ri-check-double-line text-[10px]" />
              {t('attendance.parentNotified')}
            </span>
          )}
        </div>
      </div>
    );
  };

  const renderIncidentCard = (log: IncidentLog) => {
    const type = getIncidentTypeById(log.incidentTypeId);
    const severityColor: Record<string, string> = {
      Minor: 'bg-yellow-50 border-yellow-200 text-yellow-700',
      Moderate: 'bg-orange-50 border-orange-200 text-orange-700',
      Major: 'bg-red-50 border-red-200 text-red-700',
      Serious: 'bg-red-50 border-red-200 text-red-700',
      Critical: 'bg-red-100 border-red-300 text-red-800',
      Severe: 'bg-red-50 border-red-200 text-red-700',
      Attempt: 'bg-yellow-50 border-yellow-200 text-yellow-700',
      Brief: 'bg-orange-50 border-orange-200 text-orange-700',
      Extended: 'bg-red-50 border-red-200 text-red-700',
    };
    return (
      <div key={log.id} className="p-3 rounded-xl border border-background-200 bg-background-50">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-7 h-7 flex items-center justify-center rounded-full bg-red-50 text-red-700 text-[10px] font-heading font-700 flex-shrink-0">
              {log.studentName.split(' ').map((n) => n[0]).join('')}
            </div>
            <div className="min-w-0">
              <p className="text-xs font-label font-medium text-foreground-800 truncate">{log.studentName}</p>
              <p className="text-[10px] text-foreground-400 font-label">{log.className}</p>
            </div>
          </div>
          <span className={`px-2 py-0.5 text-[10px] font-label font-medium rounded-full border flex-shrink-0 ${severityColor[log.severity] || 'bg-background-100 text-foreground-600'}`}>
            {log.severity}
          </span>
        </div>

        <div className="flex flex-wrap gap-1 mb-1.5">
          <span className="px-1.5 py-0.5 text-[10px] font-label rounded-full bg-background-100 text-foreground-500">
            {type?.label || log.incidentTypeId}
          </span>
          <span className="px-1.5 py-0.5 text-[10px] font-label rounded-full bg-background-100 text-foreground-500">
            {log.location}
          </span>
        </div>

        <p className="text-[11px] text-foreground-600 font-label mb-1.5 leading-relaxed">{log.description}</p>

        <div className="flex items-center gap-3 text-[10px] text-foreground-400 font-label flex-wrap">
          <span>{new Date(log.timestamp).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</span>
          <span>{log.createdBy}</span>
          {log.parentNotified && (
            <span className="flex items-center gap-1 text-green-600">
              <i className="ri-check-double-line text-[10px]" />
              {t('attendance.parentNotified')}
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-background-50 rounded-2xl shadow-floating max-w-3xl w-full max-h-[90vh] flex flex-col animate-[modalEnter_200ms_cubic-bezier(0.4,0,0.2,1)]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-background-200 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-secondary-100">
              <i className="ri-file-list-3-line text-secondary-700 text-lg" />
            </div>
            <div>
              <h2 className="text-base font-heading font-700 text-foreground-900">
                {activeTab === 'preInspection'
                  ? t('attendance.preInspectionLogsForDate', { date: formatDisplayDate(selectedDate) })
                  : t('attendance.incidentLogsForDate', { date: formatDisplayDate(selectedDate) })}
              </h2>
              <p className="text-xs text-foreground-400 font-label">
                {activeTab === 'preInspection'
                  ? t('attendance.preInspectionLogs')
                  : t('attendance.incidentLogs')}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Tab switcher */}
        <div className="flex items-center gap-2 px-5 py-3 border-b border-background-100 flex-shrink-0">
          <button
            onClick={() => setActiveTab('preInspection')}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-label font-medium rounded-full transition-all cursor-pointer whitespace-nowrap ${
              activeTab === 'preInspection'
                ? 'bg-blue-50 text-blue-700 border border-blue-200'
                : 'bg-background-100 text-foreground-500 hover:text-foreground-700 border border-transparent'
            }`}
          >
            <i className="ri-clipboard-line text-sm" />
            {t('attendance.preInspectionLogs')}
            <span className="px-1.5 py-0.5 text-[10px] rounded-full bg-blue-100 text-blue-600">{filteredPiLogs.length}</span>
          </button>
          <button
            onClick={() => setActiveTab('incidents')}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-label font-medium rounded-full transition-all cursor-pointer whitespace-nowrap ${
              activeTab === 'incidents'
                ? 'bg-red-50 text-red-700 border border-red-200'
                : 'bg-background-100 text-foreground-500 hover:text-foreground-700 border border-transparent'
            }`}
          >
            <i className="ri-alert-line text-sm" />
            {t('attendance.incidentLogs')}
            <span className="px-1.5 py-0.5 text-[10px] rounded-full bg-red-100 text-red-600">{filteredIncidentLogs.length}</span>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {activeTab === 'preInspection' && (
            filteredPiLogs.length === 0 ? (
              <div className="py-12 text-center">
                <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-2xl bg-blue-50 mb-4">
                  <i className="ri-clipboard-line text-blue-300 text-2xl" />
                </div>
                <p className="text-sm font-label font-medium text-foreground-500">{t('attendance.noLogsForDate')}</p>
                <p className="text-xs text-foreground-400 mt-1">{t('attendance.noLogsForDateHint')}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {filteredPiLogs.map(renderPiLogCard)}
              </div>
            )
          )}

          {activeTab === 'incidents' && (
            filteredIncidentLogs.length === 0 ? (
              <div className="py-12 text-center">
                <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-2xl bg-red-50 mb-4">
                  <i className="ri-alert-line text-red-300 text-2xl" />
                </div>
                <p className="text-sm font-label font-medium text-foreground-500">{t('attendance.noIncidentsForDate')}</p>
                <p className="text-xs text-foreground-400 mt-1">{t('attendance.noIncidentsForDateHint')}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {filteredIncidentLogs.map(renderIncidentCard)}
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}